import { Alert, Badge, Button, Group, Stack, Table, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi, rawApiFile } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatDate, formatFxRate, formatMoney, formatRateBp, toIsoDate } from '../../lib/format';
import { notifyError } from '../../lib/notify';

/**
 * What the driver is about to be handed, on the screen, before it is handed to him.
 *
 * The statement existed as two files and nothing else: the only way to see what a guide would be
 * given was to generate the PDF and read it. So the owner either handed over a sheet he had not
 * checked, or checked it by making a file he then had to throw away — and the figure he read out at
 * the bar came off a different screen from the one on the paper, which is how two true numbers end
 * up disagreeing in front of the person they are about.
 *
 * This is the same read the file is rendered from, over the same nights, so what is on the screen is
 * what comes out of the printer. One range drives both.
 *
 * ## No guest is named here either
 *
 * The JSON behind this carries `orders.tabName` — free text a bartender typed, which is routinely a
 * guest's name — and the paper deliberately does not. This table is what the guide will be handed,
 * so it shows what he will be handed: the table number that groups a split bill and the bill number
 * itself. Which guest sat there is a question for the commission history below, on the owner's own
 * ledger, and not for a sheet he may well turn the laptop round to show somebody.
 *
 * ## What is still owed is provisional and says so
 *
 * An unpaid figure is worked out again every night: a refunded bill takes commission back and
 * crossing a volume tier restates the nights before it at the better rate. Only paying closes it.
 * The sheet carries that sentence and so does this, in the same words, because a driver comparing
 * the two should not find the screen promising what the paper hedges.
 */

const STATEMENT_PATH = '/admin/v1/referrals/statement';

interface StatementLine {
  accrualId: string;
  businessDate: string | null;
  orderNumber: number | null;
  checkNumber: number | null;
  level: 'driver' | 'agency';
  kind: 'accrual' | 'reversal' | 'tierAdjustment';
  basis: 'preTaxNet' | 'gross' | null;
  baseMinor: number;
  baseCurrency: string;
  rateBp: number;
  amountMinor: number;
  currency: string;
  fxRateMicro: number | null;
  /** Handed over — the payout LINE's status, never merely that a payout list has claimed it. */
  paid: boolean;
}

interface StatementTotal {
  currency: string;
  baseMinor: number;
  /**
   * Which money the BILLS behind this section were in, or null where they were in more than one.
   * Null means there is no single figure, and the row is left out rather than shown as nothing —
   * a zero there reads as "no business", which is the opposite of what it means.
   */
  baseCurrency: string | null;
  amountMinor: number;
  unpaidMinor: number;
}

/**
 * The stretch of trade one payout covers, and the morning it is handed over.
 *
 * Not the nights above, which are whatever was typed into the picker. This is what the partner
 * agreed to be settled over — and the period a volume threshold is counted inside, so it is the
 * stretch that gets restated when he crosses one. Nothing is scheduled off `payoutDate`: it is the
 * morning the owner said he would settle, not an appointment anything here keeps.
 */
interface PayoutPeriod {
  cadence: string;
  periodStart: string;
  periodEnd: string;
  payoutDate: string;
}

interface Statement {
  profileName: string;
  from: string;
  to: string;
  /** The island's date the ledger was read on. What is still owed is only true as of this date. */
  asOf: string;
  /** Null where nobody has agreed a cadence — never a guess, and never a default. */
  period: PayoutPeriod | null;
  lines: StatementLine[];
  totals: StatementTotal[];
}

/**
 * The last thirty nights the bar traded, ending on the night it is on now.
 *
 * Not the browser's calendar. This is the sheet handed to somebody who is arguing about dates, and
 * the ledger behind it is ranged on the NIGHT — so a statement whose last day is the reader's
 * "today" either stops a night early or claims a night that has not happened, depending on which
 * country the laptop is in and what time it is.
 */
function defaultRange(businessToday: string): [Date, Date] {
  const end = new Date(`${businessToday}T12:00:00`);
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return [start, end];
}

/** A bill or a table, as a person cites one: `#3`. */
function hash(n: number | null): string {
  return n === null ? '—' : `#${n}`;
}

export function StatementSection({ profileId, partnerName }: { profileId: string; partnerName: string }) {
  // Never undefined, so the pickers are seeded on the first render instead of showing empty boxes.
  // A statement is a plain calendar range on the bar's own clock; the 04:00 roll is the report's
  // business, and `useBusinessDate` would leave the fields blank until the location landed.
  const islandToday = useIslandToday();
  const [range, setRange] = useState<[Date | null, Date | null]>(() => defaultRange(islandToday));
  const from = toIsoDate(range[0]);
  const to = toIsoDate(range[1]);
  const ready = Boolean(from && to);

  const statementQuery = useQuery({
    queryKey: listKey(STATEMENT_PATH, { profileId, from, to }),
    queryFn: () =>
      rawApi<Statement>(
        'GET',
        `${STATEMENT_PATH}?profileId=${encodeURIComponent(profileId)}&from=${from}&to=${to}`,
      ),
    enabled: Boolean(profileId) && ready,
  });
  const statement = statementQuery.data;

  const download = useMutation({
    mutationFn: async (format: 'pdf' | 'csv') => {
      const query = `profileId=${encodeURIComponent(profileId)}&from=${from}&to=${to}`;
      const blob = await rawApiFile(`${STATEMENT_PATH}/${format}?${query}`);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      anchor.download = `${partnerName} ${from} to ${to}.${format}`;
      anchor.click();
      // Long enough for the tab to have taken the bytes; the object would otherwise be held for the
      // life of the page every time somebody asks for a statement.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: (err) => notifyError(err, `make a statement for ${partnerName}`),
  });

  return (
    <Stack gap="md">
      <Group align="flex-end" gap="sm">
        <DatePickerInput
          type="range"
          label="Nights it covers"
          description="The nights the guests came in, not the days the money was banked"
          value={range}
          onChange={(value) => setRange(value as unknown as [Date | null, Date | null])}
          maw={340}
          allowSingleDateInRange
        />
        <Button
          size="xs"
          variant="light"
          disabled={!ready}
          loading={download.isPending && download.variables === 'pdf'}
          onClick={() => download.mutate('pdf')}
        >
          Statement to hand over
        </Button>
        <Button
          size="xs"
          variant="default"
          disabled={!ready}
          loading={download.isPending && download.variables === 'csv'}
          onClick={() => download.mutate('csv')}
        >
          Spreadsheet
        </Button>
      </Group>

      {/*
        WHICH PERIOD HE IS IN, beside the nights somebody chose — because they are different
        questions and the owner is answering the second one. The nights above are a range he typed;
        this is the stretch the partner is settled over and the stretch his volume is counted in.
        Silent where nobody has agreed a cadence, rather than showing a period nobody chose.
      */}
      {statement?.period ? (
        <Text size="sm" c="dimmed">
          Paid {enumLabel('payoutCadence', statement.period.cadence).toLowerCase()} — the period he is in runs{' '}
          {formatDate(statement.period.periodStart)} to {formatDate(statement.period.periodEnd)}, handed over{' '}
          {formatDate(statement.period.payoutDate)}.
        </Text>
      ) : null}

      {/*
        Told apart from an empty statement, because "nothing was earned over these nights" is a
        sentence somebody would act on — and a request that merely failed says the same thing.
      */}
      {!ready ? (
        // Half a range is not a range. Between the two clicks in the picker there is nothing to
        // ask for, and "they earned nothing" is not the answer to a question nobody has finished.
        <EmptyNote>Choose the nights this covers.</EmptyNote>
      ) : statementQuery.isError && !statement ? (
        <Alert color="orange" variant="light" title="This could not be read just now">
          Nothing has changed. Try the dates again in a moment.
        </Alert>
      ) : statementQuery.isLoading ? (
        <EmptyNote>Loading…</EmptyNote>
      ) : !statement || statement.lines.length === 0 ? (
        <EmptyNote>
          {partnerName} earned nothing over these nights. A commission appears as soon as a tab tagged to them
          is paid.
        </EmptyNote>
      ) : (
        statement.totals.map((total) => {
          const mine = statement.lines.filter((l) => l.currency === total.currency);
          return (
            // One section per money, and never a figure across two. A dollar commission and a
            // lempira one are separate debts, which is what the paper says and what the payout does.
            <Stack key={total.currency} gap="xs">
              {statement.totals.length > 1 && (
                <Text size="sm" fw={600}>
                  {total.currency === 'USD' ? 'Paid in dollars' : 'Paid in lempiras'}
                </Text>
              )}
              <Table.ScrollContainer minWidth={860}>
                <Table verticalSpacing="sm" highlightOnHover striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Night</Table.Th>
                      <Table.Th>Table</Table.Th>
                      <Table.Th>Bill</Table.Th>
                      <Table.Th>What it is</Table.Th>
                      <Table.Th ta="right">Share of</Table.Th>
                      <Table.Th ta="right">Rate</Table.Th>
                      <Table.Th ta="right">Commission</Table.Th>
                      <Table.Th>Handed over</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {mine.map((line) => (
                      <Table.Tr key={line.accrualId}>
                        <Table.Td>{formatDate(line.businessDate)}</Table.Td>
                        <Table.Td>{hash(line.orderNumber)}</Table.Td>
                        <Table.Td>{hash(line.checkNumber)}</Table.Td>
                        <Table.Td>
                          {/*
                            On every row, not only the odd ones. A negative row with nothing beside
                            it explaining what it is reads as an accusation, and printing the word
                            only where it is unusual makes its absence mean something.
                          */}
                          <Badge color={enumColor('referralEntryKind', line.kind)} variant="light">
                            {enumLabel('referralEntryKind', line.kind)}
                          </Badge>
                          {line.level === 'agency' && (
                            <Text size="xs" c="dimmed">
                              their agency&apos;s share
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td ta="right">
                          {formatMoney(line.baseMinor, line.baseCurrency)}
                          <Text size="xs" c="dimmed">
                            {line.basis ? enumLabel('referralBase', line.basis) : 'the bill'}
                            {line.fxRateMicro ? ` · at ${formatFxRate(line.fxRateMicro)}` : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">{formatRateBp(line.rateBp)}</Table.Td>
                        <Table.Td ta="right" fw={600}>
                          {formatMoney(line.amountMinor, line.currency)}
                        </Table.Td>
                        <Table.Td>
                          {line.paid ? (
                            <Badge color="teal" variant="light">
                              Paid
                            </Badge>
                          ) : (
                            <Text size="xs" c="dimmed">
                              Still owed
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                  <Table.Tfoot>
                    {[
                      // The bills themselves, in THEIR money — left out entirely where this
                      // section's bills were in two moneys, because there is no one figure and a
                      // zero would be read as no business.
                      ...(total.baseCurrency
                        ? [
                            {
                              label: 'Bills behind it',
                              value: formatMoney(total.baseMinor, total.baseCurrency),
                              strong: false,
                            },
                          ]
                        : []),
                      {
                        label: 'Earned',
                        value: formatMoney(total.amountMinor, total.currency),
                        strong: false,
                      },
                      {
                        label: 'Already paid',
                        value: formatMoney(total.amountMinor - total.unpaidMinor, total.currency),
                        strong: false,
                      },
                      {
                        label: 'Still owed',
                        value: formatMoney(total.unpaidMinor, total.currency),
                        strong: true,
                      },
                    ].map((row) => (
                      <Table.Tr key={row.label}>
                        <Table.Th colSpan={6} ta="right" fw={row.strong ? 700 : 500}>
                          {row.label}
                        </Table.Th>
                        <Table.Th ta="right" fw={row.strong ? 700 : 500}>
                          {row.value}
                        </Table.Th>
                        <Table.Th />
                      </Table.Tr>
                    ))}
                  </Table.Tfoot>
                </Table>
              </Table.ScrollContainer>
            </Stack>
          );
        })
      )}

      {/*
        The same two sentences the paper carries, deliberately word for word: the owner reads this
        screen out and then hands over the sheet, and a screen that promised what the sheet hedges
        is the disagreement the statement exists to end.
      */}
      {statement && (
        <Text size="xs" c="dimmed">
          What is still owed is as of {formatDate(statement.asOf)} and is not final until it is paid. A bill
          refunded later takes commission back; a higher rate adds to it.
        </Text>
      )}
    </Stack>
  );
}
